import { AlertCircle, Calendar, CheckCircle, ChevronLeft, ChevronRight, Circle, Plus, Search } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTestResults } from '../../hooks/useTestResults'
import { theme } from '../../theme/theme'
import type { TestResult, TestResultsFilter } from '../../types/testResults'
import { getTestType } from '../../types/testTypes'
import { getEntryIcon } from '../../utils/disciplineIcons'
import { Button } from '../common/Button'
import { TestResultModal } from '../modals/TestResultModal'
import { calculateRunPace, formatCSS } from '../modals/testResultUtils'

const Container = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  gap: ${({ theme }) => theme.spacing.md};
`

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm} 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  transition: ${({ theme }) => theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`

const FilterDropdown = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  min-width: 140px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const DisciplineChips = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`

const DisciplineChip = styled.button<{ $isActive: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.background)};
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.textInverted : theme.colors.text)};
  border-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.border)};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme, $isActive }) => ($isActive ? theme.colors.primaryDark : theme.colors.surfaceHover)};
  }
`

const StatusChips = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`

const StatusChip = styled.button<{ $isActive: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  background: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.background)};
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.textInverted : theme.colors.text)};
  border-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.border)};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme, $isActive }) => ($isActive ? theme.colors.primaryDark : theme.colors.surfaceHover)};
  }
`

const TableContainer = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHeader = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`

const TableRow = styled.tr`
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  vertical-align: middle;
`

const TestDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.xs};
  font-weight: 500;
  
  &.completed {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &.planned {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  &.cancelled {
    color: ${({ theme }) => theme.colors.error};
  }
`

const ResultValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
`

const ResultUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: 4px;
`

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const PaginationInfo = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const PageSelector = styled.select`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.sm};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

// Helper function to format time in seconds to MM:SS format
function formatTime(timeInSeconds?: number): string {
  if (!timeInSeconds) return '—'
  const minutes = Math.floor(timeInSeconds / 60)
  const seconds = timeInSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// Helper function to format multiple results for display based on test type
function formatResults(testResult: TestResult): { display: string; hasData: boolean } {
  // Don't show any results for planned tests
  if (testResult.status === 'planned') {
    return {
      display: '—',
      hasData: false,
    }
  }

  const results: string[] = []
  const testType = getTestType(testResult.testType)

  // Display specific results based on test type
  if (testType) {
    if (testType.discipline === 'run') {
      // Show total time and pace for run tests
      if (testResult.time) {
        results.push(`${formatTime(testResult.time)}`)
        const distance = testType.id === '5k' ? 5 : testType.id === '10k' ? 10 : 0
        if (distance > 0) {
          const pace = calculateRunPace(testResult.time, distance)
          results.push(`${pace}`)
        }
      }
    } else if (testType.discipline === 'bike') {
      // Show 20min W and 1h FTP for bike
      if (testResult.avgPower) {
        results.push(`${testResult.avgPower.toFixed(0)}W (20min)`)
      }
      if (testResult.ftp) {
        results.push(`${testResult.ftp}W 60min FTP`)
      }
    } else if (testType.discipline === 'swim') {
      // Show CSS for swim tests
      if (testResult.css) {
        results.push(`${formatCSS(testResult.css)} CSS over 1500m`)
      }
    }

    // Always show avg heart rate if available
    if (testResult.avgHeartRate) {
      results.push(`${testResult.avgHeartRate} bpm`)
    }
  } else {
    // Fallback for unknown test types
    if (testResult.time) {
      results.push(`${formatTime(testResult.time)}`)
    }
    if (testResult.avgPower) {
      results.push(`${testResult.avgPower.toFixed(0)}W avg`)
    }
    if (testResult.avgHeartRate) {
      results.push(`${testResult.avgHeartRate} bpm avg`)
    }
  }

  return {
    display: results.length > 0 ? results.join(', ') : '—',
    hasData: results.length > 0,
  }
}

// Helper function to get status icon
function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle size={14} />
    case 'planned':
      return <Circle size={14} />
    case 'cancelled':
      return <AlertCircle size={14} />
    default:
      return <Circle size={14} />
  }
}

interface TestResultsTableProps {
  athleteId: string
}

export const TestResultsTable: React.FC<TestResultsTableProps> = ({ athleteId }) => {
  // Use the test results hook directly
  const { testResults: data, loading, error, addTestResult, updateTestResult, deleteTestResult } = useTestResults(athleteId)
  const [filter, setFilter] = useState<TestResultsFilter>({
    search: '',
    type: '',
    status: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [disciplineFilter, setDisciplineFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showTestResultModal, setShowTestResultModal] = useState(false)
  const [editingTestResult, setEditingTestResult] = useState<TestResult | undefined>(undefined)

  const filteredAndSortedData = useMemo(() => {
    const filtered = data.filter((test) => {
      const matchesSearch =
        !filter.search ||
        test.description.toLowerCase().includes(filter.search.toLowerCase()) ||
        test.notes?.toLowerCase().includes(filter.search.toLowerCase())

      const matchesType = !filter.type || test.discipline === filter.type
      const matchesStatus = !filter.status || test.status === filter.status

      // Discipline filter
      const matchesDiscipline =
        !disciplineFilter ||
        (disciplineFilter === 'swim' && test.discipline === 'swim') ||
        (disciplineFilter === 'run' && test.discipline.includes('run')) ||
        (disciplineFilter === 'bike' && test.discipline.includes('bike'))

      return matchesSearch && matchesType && matchesStatus && matchesDiscipline
    })

    // Sort by date (reverse chronological - newest first)
    filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

    return filtered
  }, [data, filter, disciplineFilter])

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const paginatedData = filteredAndSortedData.slice(startIndex, startIndex + pageSize)

  // Reset to first page when filters change
  const resetPagination = () => setCurrentPage(1)

  const handleFilterChange = (key: keyof TestResultsFilter, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: value }))
    resetPagination()
  }

  const handleDisciplineFilterChange = (value: string) => {
    setDisciplineFilter(value)
    resetPagination()
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    resetPagination()
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const handleCloseModals = () => {
    setShowTestResultModal(false)
  }

  const onAdd = () => {
    setEditingTestResult(undefined)
    setShowTestResultModal(true)
  }

  const onEdit = (test: TestResult) => {
    setEditingTestResult(test)
    setShowTestResultModal(true)
  }

  if (loading) {
    return (
      <Container>
        <EmptyState>Loading test results...</EmptyState>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Header></Header>
        <EmptyState style={{ color: 'red' }}>Error loading test results: {error}</EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <SearchBox>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search test results..."
            value={filter.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </SearchBox>

        <DisciplineChips>
          {['all', 'run', 'bike', 'swim'].map((discipline) => (
            <DisciplineChip
              key={discipline}
              $isActive={disciplineFilter === discipline || (discipline === 'all' && !disciplineFilter)}
              onClick={() => handleDisciplineFilterChange(discipline === 'all' ? '' : discipline)}
            >
              {discipline.charAt(0).toUpperCase() + discipline.slice(1)}
            </DisciplineChip>
          ))}
        </DisciplineChips>

        <StatusChips>
          {['all', 'completed', 'planned', 'cancelled'].map((status) => (
            <StatusChip
              key={status}
              $isActive={statusFilter === status || (status === 'all' && !statusFilter)}
              onClick={() => handleStatusFilterChange(status === 'all' ? '' : status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </StatusChip>
          ))}
        </StatusChips>

        <Button variant="primary" onClick={onAdd}>
          <Plus size={16} />
          Add Test
        </Button>
      </Header>

      <TableContainer>
        {filteredAndSortedData.length === 0 ? (
          <EmptyState>{data.length === 0 ? 'No test results found.' : 'No results match your filters.'}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <TableHeader>Date</TableHeader>
                <TableHeader>Discipline</TableHeader>
                <TableHeader>Test Type</TableHeader>
                <TableHeader>Result</TableHeader>
                <TableHeader>Status</TableHeader>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((test) => {
                return (
                  <TableRow key={test.id} onClick={() => onEdit(test)} title="Click to edit test">
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: theme.typography.sm }}>
                        <Calendar size={14} color="currentColor" />
                        {new Date(test.date).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <TestDescription>
                        {React.createElement(getEntryIcon(test.discipline), { size: 18 })} {test.discipline}
                      </TestDescription>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div style={{ fontWeight: 500 }}>{getTestType(test.testType)?.name || test.testType}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ResultValue>{formatResults(test).display}</ResultValue>
                    </TableCell>
                    <TableCell>
                      <StatusBadge $status={test.status} className={test.status}>
                        {getStatusIcon(test.status)}
                        {test.status.charAt(0).toUpperCase() + test.status.slice(1)}
                      </StatusBadge>
                    </TableCell>
                  </TableRow>
                )
              })}
            </tbody>
          </Table>
        )}
      </TableContainer>

      {filteredAndSortedData.length > 0 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredAndSortedData.length)} of{' '}
            {filteredAndSortedData.length} results
          </PaginationInfo>

          <PaginationControls>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Show:</span>
            <PageSelector value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </PageSelector>

            <PaginationButton
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <ChevronLeft size={16} />
            </PaginationButton>

            <span
              style={{
                fontSize: '0.875rem',
                color: 'var(--color-text-secondary)',
                minWidth: '60px',
                textAlign: 'center',
              }}
            >
              {totalPages > 0 ? `${currentPage} / ${totalPages}` : '0 / 0'}
            </span>

            <PaginationButton
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              title="Next page"
            >
              <ChevronRight size={16} />
            </PaginationButton>
          </PaginationControls>
        </PaginationContainer>
      )}

      {/* Test Result Modal */}
      <TestResultModal
        athleteId={athleteId}
        isOpen={showTestResultModal}
        onClose={handleCloseModals}
        testResult={editingTestResult}
        addTestResult={addTestResult}
        updateTestResult={updateTestResult}
        deleteTestResult={deleteTestResult}
      />
    </Container>
  )
}
